import { useEffect, useRef, useState } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Column, Spacer, TwoColGrid } from "../Layout/Layout";
import useUser from "../../core/user/useUser";
import { notifyApiErrors } from "../../core/helpers/helpers";
import { notify } from "../../ui-lib/components/Alerts/Toast";
import PrimeModal from "../../ui-lib/components/PrimeModal/PrimeModal";
import { IObservationIndetifiersBulkNew } from "../../core/api/observationIdentifiers/types";
import {
  createObservationIdentifiers,
  resolveCreateError,
} from "../../core/api/observationIdentifiers/identifiers";
import { useObjectForm } from "../../core/SaveObjectContext/hooks";
import Dropdown from "../../ui-lib/components/Dropdown/Dropdown";
import {
  CustomDropdown,
  ITreeData,
} from "../../ui-lib/components/Hierarchy/CustomTreeDropdown/CustomDropdown";
import { getKnownLocations } from "../../core/api/locations/locations";
import { IItemProp } from "../../ui-lib/components/Dropdown/DropdownItem";
import { Button } from "../../ui-lib/components/Button/Button";
import {
  EditLocationActions,
  LocationEditForm,
} from "../location/LocationEditForm";
import ObservationIdentifierRowsSet from "./ObservationIdentifierRowsSet";
import Divider from "../../ui-lib/components/Divider/Divider";
import styles from "./ObservationIdentifiers.module.css";
import { getDefaultDropdownItems, getDefaultRowSetValues } from "./helpers";

const CreateObservationIdentifierModal = ({
  isOpen,
  locationId,
  onClose,
  onUpdate,
}: {
  isOpen: boolean;
  locationId?: number;
  onClose: () => void;
  onUpdate: () => void;
}) => {
  const { t } = useTranslation();
  const { authenticatedRequest } = useUser();

  const [isLoading, setIsLoading] = useState(false);
  const [isLocationsLoading, setIsLocationsLoading] = useState(false);
  const [organisationItemId, setOrganisationItemId] = useState<string>();
  const [locationItemId, setLocationItemId] = useState<string>(
    locationId?.toString() || "",
  );
  const [locationItems, setLocationItems] = useState<IItemProp[]>([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const editLocationRef = useRef<EditLocationActions>(null);
  const fallbackDescription = t("Locations:unknown_description");
  const defaultRowSetValue = getDefaultRowSetValues();
  const { selectedRoom } = getDefaultDropdownItems(t);
  const methods = useObjectForm<IObservationIndetifiersBulkNew>({
    defaultValues: defaultRowSetValue,
  });

  const resetForm = () => {
    methods.reset(defaultRowSetValue);
    setOrganisationItemId("");
    setLocationItemId(locationId?.toString() || "");
    setLocationItems([]);
  };

  const onSubmit = async (values: IObservationIndetifiersBulkNew) => {
    setIsLoading(true);
    try {
      await createObservationIdentifiers(
        locationItemId,
        values,
        authenticatedRequest,
      );

      notify({
        message: t("Locations:oi_create_success"),
      });
      onUpdate();
      onClose();
    } catch (error: any) {
      const apiErrors = resolveCreateError(values.dataDefinitions, error);
      apiErrors.forEach((apiError) => {
        methods.setError(apiError.field, { ...apiError });
      });
      notifyApiErrors(error.response?.data?.errors);
    } finally {
      setIsLoading(false);
    }
  };

  const onNewRoomSubmit = async () => {
    setIsLoading(true);
    try {
      const result = await editLocationRef.current?.submitForm();
      if (!result?.status || !result.data.id) return;

      const id = result.data.id.toString();
      setLocationItems((prev) => [
        ...prev,
        {
          id: id,
          name: result.data.description || fallbackDescription,
          isSelected: true,
        },
      ]);
      notify({
        message: t("Locations:location_added"),
      });
      setLocationItemId(id);
      setIsCreateModalOpen(false);
      methods.setValue("locationId", id);
      methods.trigger("locationId");
    } catch (error: any) {
      notifyApiErrors(error.response?.data?.errors, error.response?.status);
    } finally {
      setIsLoading(false);
    }
  };

  const onOrganisationSelect = async (data?: ITreeData) => {
    const orgId = data?.key?.toString();
    if (orgId) {
      setIsLocationsLoading(true);
      setOrganisationItemId(orgId);
      methods.setValue("organizationId", orgId);
      methods.trigger("organizationId");
      setLocationItemId("");
      methods.setValue("locationId", "");
      methods.clearErrors("locationId");
      setLocationItems([]);
      try {
        const rooms = await getKnownLocations(
          {
            organisationId: orgId,
            o: "DESCRIPTION_ASC",
            klType: "10",
            page: 1,
            pageSize: 5000,
          },
          authenticatedRequest,
        );
        setLocationItems(
          rooms.knownLocations.map((room) => ({
            name: room.description || fallbackDescription,
            id: room.id.toString(),
            isSelected: room.id.toString() === locationItemId,
          })),
        );
      } catch (error: any) {
        setLocationItems([]);
        notifyApiErrors(error.response?.data?.errors);
      } finally {
        setIsLocationsLoading(false);
      }
    } else {
      resetForm();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  return (
    <>
      <PrimeModal
        withFooter
        withHeader
        onClose={() => setIsCreateModalOpen(false)}
        isOpen={isCreateModalOpen}
        header={t("Objects:accessories_add_location_modal_title")}
        loading={isLoading}
        submitBtn={{
          text: t("Objects:accessories_add_location_modal_save"),
          onClick: onNewRoomSubmit,
        }}
        cancelBtn={{
          text: t("Common:cancel"),
          onClick: () => setIsCreateModalOpen(false),
        }}
        fixedWidth="848px"
      >
        <p>{t("Locations:create_new_description")}</p>
        <Spacer size={16} />
        {organisationItemId && (
          <LocationEditForm
            ref={editLocationRef}
            roomData={{
              organizationId: Number.parseInt(organisationItemId),
              klType: "ROOM",
            }}
          />
        )}
      </PrimeModal>
      <PrimeModal
        withFooter
        withHeader
        loading={isLoading}
        header={t("Locations:oi_add_identifier_title")}
        fixedWidth="848px"
        isOpen={isOpen}
        onClose={onClose}
        submitBtn={{
          text: t("Locations:oi_add_identifier"),
          onClick: methods.handleSubmit(onSubmit),
        }}
        cancelBtn={{ text: t("Common:cancel"), onClick: onClose }}
      >
        <Column type="top" align="start">
          <p>{t("Locations:oi_add_identifier_description")}</p>
          <Spacer size={16} />
          {!locationId && (
            <>
              <TwoColGrid columnGap={32}>
                <Dropdown
                  disabled={true}
                  title={t("Locations:oi_labels_location_type")}
                  id="locationType"
                  items={[selectedRoom]}
                  selectedItem={selectedRoom}
                  onSelectItem={() => {}}
                />
                <Controller
                  name="organizationId"
                  control={methods.control}
                  rules={{ required: true }}
                  render={() => (
                    <CustomDropdown
                      required
                      selectionMode="single"
                      fullWidth
                      label={t("Locations:organisation")}
                      placeholder={t("Common:select_organisation")}
                      onSelectSignle={onOrganisationSelect}
                      validationError={
                        methods.formState.errors.organizationId &&
                        t("Errors:input_field_required")
                      }
                    />
                  )}
                />
              </TwoColGrid>
              <Spacer size={16} />
              <TwoColGrid columnGap={32}>
                <Column align="start">
                  <Controller
                    name="locationId"
                    control={methods.control}
                    rules={{ required: true }}
                    render={() => (
                      <Dropdown
                        required
                        title={t("Locations:oi_labels_room_description")}
                        disabled={isLocationsLoading || !organisationItemId}
                        items={locationItems}
                        isLoading={isLocationsLoading}
                        maxVisible={5}
                        placeholder={t("Locations:oi_select_placeholder_room")}
                        selectedItem={locationItems.find(
                          (r) => r.id === locationItemId,
                        )}
                        onSelectItem={(item) => {
                          setLocationItemId(item.id.toString());
                          methods.setValue("locationId", item.id);
                          methods.trigger("locationId");
                        }}
                        validationError={
                          methods.formState.errors.locationId &&
                          t("Errors:input_field_required")
                        }
                      />
                    )}
                  />
                  {!organisationItemId && (
                    <p>{t("Locations:oi_select_organisation_first")}</p>
                  )}
                </Column>
                {organisationItemId && (
                  <Column className="h-100" type="bottom">
                    <Button
                      variant="link"
                      className={styles.buttonLink}
                      text={t("Locations:oi_create_new_room")}
                      onClick={() => {
                        setIsCreateModalOpen(true);
                      }}
                    />
                  </Column>
                )}
              </TwoColGrid>
              <Spacer size={16} />
              <Divider />
              <Spacer size={16} />
            </>
          )}
          <FormProvider {...methods}>
            <ObservationIdentifierRowsSet hasLocation={!!locationItemId} />
          </FormProvider>
        </Column>
      </PrimeModal>
    </>
  );
};

export default CreateObservationIdentifierModal;
